import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect, useContext } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useMutation } from "@tanstack/react-query";
import Waiting from "../../assets/waitingRoom.png";
import useScrollDetection from "../../hooks/useScrollDetection";
import HealthInformationCarousel from "../../components/carousel/HealthInformationCarousel";
import HOC from "../../components/HOC";
import { useTinodeConnection } from "../../hooks/useTinodeConnection";
import { createConsultationGroupWithUsers } from "../../services/tinodeConsultationGroupService";
import {
  searchDoctorForConsultation,
  syncConsultationTopic,
} from "../../services/telemedicineApi";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import {
  readSafeStorage,
  writeSafeStorage,
} from "../../utils/localStorageHelper";

const WAITING_CONSULTATION_STORAGE_KEY = "waiting_consultation_id";

const getErrorMessage = (error, fallbackMessage) => {
  if (error?.message?.id) {
    return error.message.id;
  }
  if (error?.message?.en) {
    return error.message.en;
  }
  if (error?.message) {
    return error.message;
  }
  return fallbackMessage;
};

const isTinodeAuthError = (errOrMsg) => {
  const raw =
    typeof errOrMsg === "string"
      ? errOrMsg
      : getErrorMessage(errOrMsg, "") || "";
  const msg = raw.toLowerCase();
  return (
    msg.includes("authentication required") ||
    msg.includes("session habis") ||
    msg.includes("silakan login")
  );
};

const WaitingRoomConsultation = () => {
  const { setTopicId } = useContext(GlobalStateContext);

  const navigate = useNavigate();
  const location = useLocation();
  const isScrolled = useScrollDetection(5);
  const [dots, setDots] = useState("");
  const [consultationId, setConsultationId] = useState("");
  const [doctorSearchResult, setDoctorSearchResult] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const user = useMemo(
    () => ({
      fullname: readSafeStorage("fullname", "User"),
      email: readSafeStorage("email", ""),
      id: readSafeStorage("user_id", ""),
      userid_tinode: readSafeStorage("user_id", ""),
    }),
    [],
  );

  const { tinodeRef, isConnected, isReady, isLoading, error, retryConnection } =
    useTinodeConnection(user);
  const isTinodeReady =
    isConnected &&
    isReady &&
    !!tinodeRef.current &&
    typeof tinodeRef.current.isAuthenticated === "function" &&
    tinodeRef.current.isAuthenticated();

  const redirectToLogin = () => {
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;
    writeSafeStorage("LastPath", currentPath);
    navigate("/login");
  };

  useEffect(() => {
    const consultationIdFromState = location.state?.consultationId || "";
    const consultationIdFromStorage =
      sessionStorage.getItem(WAITING_CONSULTATION_STORAGE_KEY) || "";
    const nextConsultationId =
      consultationIdFromState || consultationIdFromStorage || "";

    if (nextConsultationId) {
      sessionStorage.setItem(
        WAITING_CONSULTATION_STORAGE_KEY,
        nextConsultationId.toString(),
      );
      setConsultationId(nextConsultationId.toString());
    }
  }, [location.state]);

  const { mutate: mutateSearchDoctor, isPending: isSearchingDoctor } =
    useMutation({
      mutationFn: searchDoctorForConsultation,
      onSuccess: (result) => {
        setDoctorSearchResult(result);
        setSearchError("");
      },
      onError: (error) => {
        setDoctorSearchResult(null);
        setSearchError(
          getErrorMessage(error, "Gagal mencari dokter. Silakan coba lagi."),
        );
      },
    });

  useEffect(() => {
    if (!consultationId) {
      return;
    }

    mutateSearchDoctor({
      consultation_id: consultationId,
    });
  }, [consultationId, mutateSearchDoctor]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!error) return;
    if (!isTinodeAuthError(error)) return;
    redirectToLogin();
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetrySearchDoctor = () => {
    if (!consultationId || isSearchingDoctor) {
      return;
    }

    mutateSearchDoctor({
      consultation_id: consultationId,
      internal_only: false,
    });
  };

  const handleCreateGroup = async (event) => {
    event.preventDefault();

    if (!tinodeRef.current || !isConnected) {
      alert("Belum terhubung ke chat server. Silakan tunggu sebentar.");
      return;
    }

    if (!isTinodeReady) {
      alert(
        "Autentikasi chat belum siap. Silakan tunggu sebentar lalu coba lagi.",
      );
      return;
    }

    if (!doctorSearchResult) {
      alert("Data dokter belum tersedia. Silakan cari dokter terlebih dahulu.");
      return;
    }

    if (!doctorSearchResult.doctor?.tinode_uid) {
      alert("Tinode UID dokter tidak ditemukan.");
      return;
    }

    setIsCreating(true);
    try {
      const result = await createConsultationGroupWithUsers(
        tinodeRef.current,
        [doctorSearchResult.doctor.tinode_uid],
        {
          doctorName: doctorSearchResult.doctor?.name || "",
          doctorProfile: doctorSearchResult.doctor?.profile_image || "",
          doctorSip: doctorSearchResult.doctor?.no_sip || "",
          doctorSpecialty: doctorSearchResult.doctor?.speciality || "",
          customerName:
            doctorSearchResult.patient?.name ||
            readSafeStorage("fullname", "") ||
            "Pasien",
          customerProfile: doctorSearchResult.patient?.profile_image || "",
          customerAge: Number(doctorSearchResult.patient?.age || 0),
          customerGender: doctorSearchResult.patient?.gender || 0,
          customerType: doctorSearchResult.patient?.other_person ? 2 : 1,
        },
      );

      setTopicId(result.topicName);

      if (!result?.topicName) {
        throw new Error("Topic konsultasi tidak ditemukan dari Tinode.");
      }

      await syncConsultationTopic({
        consultation_id: consultationId,
        topic_name: result.topicName,
      });

      navigate("/consultation/chat-with-doctor", {
        state: {
          type: 1,
          topicName: result.topicName,
          groupName: result.groupName,
          doctorId: doctorSearchResult.doctor?.tinode_uid || "",
          invitedUsers: result.invitedUsers,
          consultationData: {
            consultationId,
            topicName: result.topicName,
            doctorId: doctorSearchResult.doctor?.id || 0,
            patientId: doctorSearchResult.patient?.id || 0,
          },
        },
      });
    } catch (error) {
      console.log("INI ERROR DARI WAITING ROOM CONSULTATION", error);
      if (isTinodeAuthError(error)) {
        alert("Sesi chat berakhir. Silakan login kembali.");
        redirectToLogin();
        return;
      }
      alert(getErrorMessage(error, "Gagal membuat room chat konsultasi."));
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusMessage = () => {
    if (!consultationId) {
      return "ID konsultasi tidak ditemukan. Silakan ulangi proses pembayaran.";
    }
    if (isSearchingDoctor) {
      return "Sedang mencari dokter yang tersedia" + dots;
    }
    if (isCreating) {
      return "Sedang menyiapkan ruang chat konsultasi" + dots;
    }
    if (searchError) {
      return searchError;
    }
    if (doctorSearchResult?.doctor) {
      return `Dokter ${doctorSearchResult.doctor.name} siap melayani. Tekan tombol mulai konsultasi untuk masuk chat.`;
    }
    return "Silakan tekan tombol untuk memulai konsultasi.";
  };

  const isButtonDisabled =
    isCreating ||
    isSearchingDoctor ||
    !isTinodeReady ||
    !doctorSearchResult?.doctor?.tinode_uid;

  return (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
            <div
              className={`bg-white fixed z-[9999] top-0 left-0 px-4 py-3 w-full flex flex-col items-center transition-all duration-300 ${
                isScrolled ? "shadow-md rounded-b-3xl" : ""
              }`}
            >
              <div className="flex flex-col w-full gap-4">
                <div className="w-full flex items-center gap-2 lg:hidden text-xl">
                  <div
                    onClick={() => navigate("/")}
                    className="p-1 hover:bg-gray-200 rounded-full"
                  >
                    <IoArrowBack className="text-gray-500" size={20} />
                  </div>
                  <p className="font-semibold text-slate-700">
                    Mulai Konsultasi
                  </p>
                </div>
              </div>
            </div>

            <div className="py-12">
              <div className="flex flex-col">
                <div className="flex flex-col items-center my-4">
                  <h1 className="font-semibold text-slate-700 text-xl">
                    {isSearchingDoctor || isCreating
                      ? "Tunggu Sebentar"
                      : "Cari Dokter"}
                  </h1>
                  <p className="text-sm text-gray-500 mb-4">
                    {!isConnected
                      ? "Chat server offline"
                      : isTinodeReady
                        ? "Chat server online"
                        : error
                          ? "Gagal terhubung ke chat server"
                          : "Chat server online, autentikasi..."}
                  </p>
                  <p className="text-slate-500 text-md font-medium text-center">
                    {getStatusMessage()}
                  </p>
                </div>

                <div className="mb-6">
                  <img
                    src={Waiting}
                    alt="Waiting Room"
                    className="w-full h-full"
                  />
                </div>

                {searchError ? (
                  <button
                    type="button"
                    disabled={!consultationId || isSearchingDoctor}
                    onClick={handleRetrySearchDoctor}
                    className="h-[40px] border text-secondary flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none border-secondary bg-white disabled:border-slate-300 disabled:text-slate-300 disabled:cursor-default mb-2"
                  >
                    Coba Cari Dokter Lagi
                  </button>
                ) : null}

                {(isConnected && !isTinodeReady) || error ? (
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={retryConnection}
                    className="h-[40px] border text-secondary flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none border-secondary bg-white disabled:border-slate-300 disabled:text-slate-300 disabled:cursor-default mb-2"
                  >
                    {isLoading
                      ? "Menghubungkan..."
                      : "Coba Lagi / Refresh Koneksi"}
                  </button>
                ) : null}

                <button
                  type="button"
                  disabled={isButtonDisabled}
                  onClick={handleCreateGroup}
                  className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default mb-6"
                >
                  {isSearchingDoctor
                    ? "Mencari Dokter"
                    : isCreating
                      ? "Menyiapkan Room Chat"
                      : "Mulai Konsultasi Dengan Dokter"}
                </button>

                <HealthInformationCarousel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HOC(WaitingRoomConsultation, "required-auth");
